import { useEffect, useState } from "react";
import { useParams, useNavigate, useBlocker } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";
import { apiClient, ApiError } from "../lib/apiClient";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../i18n/I18nProvider";
import { errorMessage } from "../i18n/apiErrors";
import { getCurrentIsoWeek, addWeeks } from "../lib/isoWeek";
import { shouldPromptOnLeave, isLeavingPlanRoute } from "./weeklyPlan/helpers";
import ConfirmDialog from "../components/ConfirmDialog";
import PlanAppBar from "./weeklyPlan/PlanAppBar";
import PlanSummary from "./weeklyPlan/PlanSummary";
import DayCard from "./weeklyPlan/DayCard";
import TripDetailsDrawer from "./weeklyPlan/TripDetailsDrawer";
import CommentsDrawer from "./weeklyPlan/CommentsDrawer";
import { DAY_LABEL_KEYS } from "./weeklyPlan/helpers";
import type {
  ChildrenResponse,
  DayView,
  GroupDetailsResponse,
  MemberChildrenResponse,
  NoteAction,
  SimpleChild,
  TripAction,
  TripView,
  WeeklyPlan,
} from "./weeklyPlan/types";

// (Diese Ansicht hält alle Fehler als Fehler-OBJEKT: Fetch-/Create-Fehler werden
// direkt über `errorMessage(t, err)` aufgelöst, transiente Mutationsfehler als
// Snackbar. Es gibt keine lokalen Key-basierten Formular-Fehlerzustände, daher
// keine DisplayError-Union wie in GroupList/ProfilePage — Req 12.6.)

// Beschreibt, welcher Drawer gerade offen ist.
type Sheet =
  | { kind: "trip"; dayIndex: number; direction: "morning" | "afternoon"; userId?: string; name?: string }
  | { kind: "comments"; dayIndex: number }
  | null;

export default function WeeklyPlanView() {
  const { id, week } = useParams<{ id: string; week?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { t } = useI18n();

  const groupId = id ?? "";
  const isoWeek = week ?? getCurrentIsoWeek();
  // Die laufende Woche darf nicht gelöscht werden (vergangene/künftige schon).
  const isCurrentWeek = isoWeek === getCurrentIsoWeek();
  const selfId = user?.id;

  // Snackbar (ersetzt den früheren hand-gerollten Toast).
  const [snack, setSnack] = useState<
    { variant: "success" | "error"; message: string } | null
  >(null);
  function showSnack(variant: "success" | "error", message: string) {
    setSnack({ variant, message });
  }

  // Admin-Zustand: „Admin-Ansicht" und die aktuell bearbeitete Person (`actAs`).
  const [adminView, setAdminView] = useState(false);
  const [actAs, setActAs] = useState<string | undefined>(undefined); // undefined = self

  // Offener Drawer.
  const [sheet, setSheet] = useState<Sheet>(null);

  // Bestätigung „Woche löschen" (nur Admin, nur bei vorhandenem Plan).
  const [confirmDeleteWeek, setConfirmDeleteWeek] = useState(false);

  // „Schmutzig"-Flag: Hat der Nutzer die aktuell angezeigte Woche geändert?
  // Wird in den onSuccess der plan-ändernden Mutationen (Trip/Kommentar)
  // gesetzt und beim Wechsel der Woche/Gruppe zurückgesetzt. Grundlage für den
  // „Benachrichtigen beim Verlassen"-Prompt (siehe Aufgabe 13.3).
  const [hasChanged, setHasChanged] = useState(false);

  // Zurücksetzen, sobald eine andere Woche (oder Gruppe) betrachtet wird: der
  // Prompt bezieht sich immer nur auf die gerade angezeigte Woche.
  useEffect(() => {
    setHasChanged(false);
  }, [groupId, isoWeek]);

  // Weekly plan (new trip-based shape).
  const {
    data: plan,
    isLoading,
    error,
  } = useQuery<WeeklyPlan | null>({
    queryKey: ["plan", groupId, isoWeek],
    queryFn: async () => {
      try {
        const res = await apiClient.get<{ plan: WeeklyPlan }>(
          `/api/groups/${groupId}/plans/${isoWeek}`,
        );
        return res.plan;
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    enabled: groupId !== "",
  });

  // Group details — liefert Admin-Rechte + aktive Mitglieder.
  const { data: groupDetails } = useQuery<GroupDetailsResponse>({
    queryKey: ["group", groupId],
    queryFn: () => apiClient.get<GroupDetailsResponse>(`/api/groups/${groupId}`),
    enabled: groupId !== "",
  });

  const members = groupDetails?.members ?? [];
  const isAdmin =
    user != null && members.some((m) => m.userId === user.id && m.role === "admin");
  const activeMembers = members.filter((m) => m.isActive);

  // Die aktuell angezeigte/bearbeitete Person.
  const displayedUserId = actAs ?? selfId;
  const isSelf = displayedUserId === selfId;
  const displayedName =
    isSelf
      ? user?.name ?? ""
      : activeMembers.find((m) => m.userId === displayedUserId)?.name ?? "";
  const editingForName = isSelf ? null : displayedName;

  // Eigene Kinder (für die eigene Sicht).
  const { data: childrenData } = useQuery<ChildrenResponse>({
    queryKey: ["children"],
    queryFn: () => apiClient.get<ChildrenResponse>(`/api/children`),
  });

  // Kinder der aktuell bearbeiteten Person (Admin „bearbeiten für") für die
  // Kinder-Auswahl direkt auf der Tageskarte.
  const { data: displayedMemberChildrenData } = useQuery<MemberChildrenResponse>({
    queryKey: ["member-children", groupId, displayedUserId],
    queryFn: () =>
      apiClient.get<MemberChildrenResponse>(
        `/api/groups/${groupId}/members/${displayedUserId}/children`,
      ),
    enabled: isAdmin && !isSelf && displayedUserId != null,
  });
  const displayedChildren: SimpleChild[] = isSelf
    ? (childrenData?.children ?? []).map((c) => ({ id: c.id, name: c.name }))
    : (displayedMemberChildrenData?.children ?? []).map((c) => ({ id: c.id, name: c.name }));

  const createPlan = useMutation({
    mutationFn: () =>
      apiClient.post<{ plan: WeeklyPlan }>(`/api/groups/${groupId}/plans`, { isoWeek }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plan", groupId, isoWeek] });
    },
  });

  // Einzel-Mutation für alle Trip-Schreibzugriffe.
  const tripMutation = useMutation({
    mutationFn: (action: TripAction): Promise<void> => {
      const base = `/api/groups/${groupId}/trips/${action.tripId}`;
      switch (action.kind) {
        case "setOwn":
          return apiClient.put<void>(`${base}/availability`, { status: action.status });
        case "clearOwn":
          return apiClient.delete<void>(`${base}/availability`);
        case "setMember":
          return apiClient.put<void>(`${base}/availability/${action.userId}`, {
            status: action.status,
          });
        case "clearMember":
          return apiClient.delete<void>(`${base}/availability/${action.userId}`);
        case "mode":
          return apiClient.patch<void>(base, { transportMode: action.transportMode });
        case "details": {
          const body: { startTime?: string | null; meetingPoint?: string | null } = {};
          if (action.startTime !== undefined) body.startTime = action.startTime;
          if (action.meetingPoint !== undefined) body.meetingPoint = action.meetingPoint;
          return apiClient.patch<void>(base, body);
        }
        case "setChild":
          return apiClient.put<void>(`${base}/children/${action.childId}`);
        case "clearChild":
          return apiClient.delete<void>(`${base}/children/${action.childId}`);
      }
    },
    onSuccess: (_data, action) => {
      queryClient.invalidateQueries({ queryKey: ["plan", groupId, isoWeek] });
      // Eine erfolgreiche Trip-Änderung macht die angezeigte Woche „schmutzig".
      setHasChanged(true);
      let message: string;
      switch (action.kind) {
        case "mode":
          message = t("weeklyPlan.toast.modeUpdated");
          break;
        case "details":
          message = t("weeklyPlan.toast.detailsUpdated");
          break;
        case "setChild":
        case "clearChild":
          message = t("weeklyPlan.toast.childrenUpdated");
          break;
        case "setMember":
        case "clearMember":
          // Vom Admin für ein anderes Mitglied gesetzt → personalisierte Meldung.
          message = t("weeklyPlan.admin.setFor", { name: action.name });
          break;
        default:
          message = t("weeklyPlan.toast.availabilityUpdated");
      }
      showSnack("success", message);
    },
    onError: (err) => {
      showSnack("error", errorMessage(t, err));
    },
  });

  // Separate Mutation für Tages-Kommentare (intern: notes).
  const noteMutation = useMutation({
    mutationFn: (action: NoteAction): Promise<unknown> => {
      switch (action.kind) {
        case "addNote":
          return apiClient.post<unknown>(
            `/api/groups/${groupId}/plans/${isoWeek}/days/${action.date}/notes`,
            { content: action.content },
          );
        case "deleteNote":
          return apiClient.delete<void>(`/api/groups/${groupId}/notes/${action.noteId}`);
      }
    },
    onSuccess: (_data, action) => {
      queryClient.invalidateQueries({ queryKey: ["plan", groupId, isoWeek] });
      // Auch ein Kommentar (hinzufügen/löschen) ändert die angezeigte Woche.
      setHasChanged(true);
      showSnack(
        "success",
        action.kind === "addNote"
          ? t("weeklyPlan.toast.noteAdded")
          : t("weeklyPlan.toast.noteDeleted"),
      );
    },
    onError: (err) => {
      showSnack("error", errorMessage(t, err));
    },
  });

  // Die manuelle Benachrichtigen-Mutation. Der Glocken-Button in der Kopfzeile
  // wurde entfernt; diese Mutation wird vom „Benachrichtigen beim Verlassen"-
  // Prompt wiederverwendet (siehe folgende Aufgabe).
  const notifyMutation = useMutation({
    mutationFn: (): Promise<unknown> =>
      apiClient.post<{ sent: boolean }>(
        `/api/groups/${groupId}/plans/${isoWeek}/notify`,
        {},
      ),
    onSuccess: () => {
      showSnack("success", t("weeklyPlan.toast.notified"));
    },
    onError: (err) => {
      showSnack("error", errorMessage(t, err));
    },
  });
  // Entscheidung, ob der „Benachrichtigen beim Verlassen"-Prompt beim Verlassen
  // der Route gezeigt werden soll. Reine Vorab-Berechnung aus der Gruppen-
  // Einstellung und dem „Schmutzig"-Flag; die eigentliche Verdrahtung mit
  // `useBlocker`/`ConfirmDialog` folgt in Aufgabe 13.3.
  const notifyOnLeave = shouldPromptOnLeave(
    groupDetails?.group.notifyOnChangeEnabled ?? false,
    hasChanged,
  );

  // „Benachrichtigen beim Verlassen"-Prompt: blockiert In-App-Navigation, die
  // den Wochenplan-Bereich der Gruppe verlässt (inkl. Back/POP), aber nur wenn
  // die Gruppen-Einstellung aktiv ist UND die angezeigte Woche geändert wurde.
  // Reine Wochenwechsel bleiben im Plan-Präfix und werden NICHT blockiert
  // (Req 5.1–5.3). react-router v7 `useBlocker` greift ausschließlich bei
  // In-App-Routenwechseln, nie beim Full-Page-Unload.
  const blocker = useBlocker(
    ({ nextLocation }) =>
      notifyOnLeave && isLeavingPlanRoute(groupId, nextLocation.pathname),
  );
  const notifyPromptOpen = blocker.state === "blocked";

  // Bestätigen → Snapshot für die aktuelle Woche auslösen, dann Navigation
  // fortsetzen (Req 5.4). Die Mutation ist serverseitig „fire-and-forget"; wir
  // warten nicht auf ihr Ergebnis, bevor wir weiter navigieren.
  function confirmNotifyAndLeave() {
    notifyMutation.mutate();
    if (blocker.state === "blocked") blocker.proceed();
  }
  // Ablehnen → ohne Benachrichtigung weiter navigieren (Req 5.5).
  function declineNotifyAndLeave() {
    if (blocker.state === "blocked") blocker.proceed();
  }
  // Dialog verwerfen/abbrechen → auf der Seite bleiben.
  function cancelNotifyPrompt() {
    if (blocker.state === "blocked") blocker.reset();
  }

  // „Woche löschen" (Admin). Löscht den Wochenplan; Cascade erfolgt serverseitig.
  const deletePlan = useMutation({
    mutationFn: (): Promise<void> =>
      apiClient.delete<void>(`/api/groups/${groupId}/plans/${isoWeek}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plan", groupId, isoWeek] });
      setConfirmDeleteWeek(false);
      showSnack("success", t("weeklyPlan.toast.weekDeleted"));
    },
    onError: (err) => {
      showSnack("error", errorMessage(t, err));
    },
  });

  function goToWeek(delta: number) {
    navigate(`/groups/${groupId}/plan/${addWeeks(isoWeek, delta)}`);
  }

  const days: DayView[] = plan?.days ?? [];
  const dayLabels = DAY_LABEL_KEYS.map((k) => t(k));

  // Für die Drawers: die aktuell adressierte Fahrt + Person.
  const activeTrip: TripView | null = (() => {
    if (sheet?.kind !== "trip") return null;
    const d = days[sheet.dayIndex];
    if (!d) return null;
    return sheet.direction === "afternoon" ? d.afternoon : d.morning;
  })();
  const sheetDayLabel =
    sheet != null ? dayLabels[sheet.dayIndex] ?? dayLabels[0] : "";

  const commentsDay: DayView | null =
    sheet?.kind === "comments" ? days[sheet.dayIndex] ?? null : null;

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "background.default" }}>
      <PlanAppBar
        groupId={groupId}
        isoWeek={isoWeek}
        days={days}
        displayedUserId={displayedUserId}
        dayLabels={dayLabels}
        onPrevWeek={() => goToWeek(-1)}
        onNextWeek={() => goToWeek(1)}
        editingForName={editingForName}
        onEndEditing={() => setActAs(undefined)}
        canDelete={isAdmin && plan != null && !isCurrentWeek}
        onDelete={() => setConfirmDeleteWeek(true)}
      />

      {/* Loading */}
      {isLoading && (
        <Stack alignItems="center" sx={{ py: 6 }} aria-live="polite">
          <CircularProgress aria-label={t("weeklyPlan.loading")} />
          <Typography sx={{ mt: 2 }} color="text.secondary">
            {t("weeklyPlan.loading")}
          </Typography>
        </Stack>
      )}

      {/* Fetch error */}
      {!isLoading && error != null && (
        <Box sx={{ m: 2 }}>
          <Alert severity="error">
            {error instanceof ApiError ? errorMessage(t, error) : t("weeklyPlan.loadError")}
          </Alert>
        </Box>
      )}

      {/* No plan for this week */}
      {!isLoading && error == null && plan == null && (
        <Box sx={{ m: 2, textAlign: "center" }}>
          <Typography fontWeight={600} sx={{ mb: 0.5 }}>
            {t("weeklyPlan.empty.title")}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t("weeklyPlan.empty.hint", { week: isoWeek })}
          </Typography>
          <Button
            variant="contained"
            onClick={() => createPlan.mutate()}
            disabled={createPlan.isPending}
            sx={{ minHeight: 44 }}
          >
            {createPlan.isPending
              ? t("weeklyPlan.create.submitting")
              : t("weeklyPlan.create.submit")}
          </Button>
          {createPlan.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {createPlan.error instanceof ApiError
                ? errorMessage(t, createPlan.error)
                : t("weeklyPlan.create.error")}
            </Alert>
          )}
        </Box>
      )}

      {/* Plan */}
      {!isLoading && error == null && plan != null && (
        <Box sx={{ maxWidth: 1100, mx: "auto" }}>
          <PlanSummary
            days={days}
            displayedUserId={displayedUserId}
            editingForName={editingForName}
          />

          {isAdmin && (
            <FormControlLabel
              sx={{ mx: 2, mt: 1 }}
              control={
                <Switch
                  checked={adminView}
                  onChange={(e) => {
                    setAdminView(e.target.checked);
                    setActAs(undefined);
                  }}
                />
              }
              label={t("weeklyPlan.admin.view")}
            />
          )}

          {adminView && (
            <Stack direction="row" gap={1} flexWrap="wrap" mx={2} mt={1}>
              {activeMembers.map((m) => {
                const self = m.userId === selfId;
                const selected = (actAs ?? selfId) === m.userId;
                return (
                  <Chip
                    key={m.userId}
                    label={self ? t("weeklyPlan.admin.selfLabel") : m.name}
                    color={self ? "primary" : "secondary"}
                    variant={selected ? "filled" : "outlined"}
                    onClick={() => setActAs(self ? undefined : m.userId)}
                    sx={{ height: 44, fontSize: 15, px: 1 }}
                  />
                );
              })}
            </Stack>
          )}

          <Box pb={5}>
            {days.map((day, i) => (
              <DayCard
                key={day.date ?? i}
                index={i}
                label={dayLabels[i] ?? dayLabels[dayLabels.length - 1]}
                day={day}
                displayedUserId={displayedUserId}
                displayedName={displayedName}
                isSelf={isSelf}
                adminView={adminView}
                displayedChildren={displayedChildren}
                disabled={tripMutation.isPending}
                onAction={(action) => tripMutation.mutate(action)}
                onOpenDetails={(trip) =>
                  setSheet({ kind: "trip", dayIndex: i, direction: trip.direction })
                }
                onOpenPersonDetails={(trip, uid, name) =>
                  setSheet({
                    kind: "trip",
                    dayIndex: i,
                    direction: trip.direction,
                    userId: uid,
                    name,
                  })
                }
                onOpenComments={() => setSheet({ kind: "comments", dayIndex: i })}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Details-Drawer */}
      <TripDetailsDrawer
        open={sheet?.kind === "trip"}
        trip={activeTrip}
        dayLabel={sheetDayLabel}
        disabled={tripMutation.isPending}
        onAction={(action) => tripMutation.mutate(action)}
        onClose={() => setSheet(null)}
      />

      {/* Kommentar-Drawer */}
      <CommentsDrawer
        open={sheet?.kind === "comments"}
        day={commentsDay}
        dayLabel={sheetDayLabel}
        userId={selfId}
        isAdmin={isAdmin}
        disabled={noteMutation.isPending}
        onNote={(action) => noteMutation.mutate(action)}
        onClose={() => setSheet(null)}
      />

      {/* „Woche löschen" Bestätigung */}
      <ConfirmDialog
        open={confirmDeleteWeek}
        title={t("weeklyPlan.delete.button")}
        message={t("weeklyPlan.delete.warning")}
        confirmLabel={t("weeklyPlan.delete.button")}
        pending={deletePlan.isPending}
        onCancel={() => setConfirmDeleteWeek(false)}
        onConfirm={() => deletePlan.mutate()}
      />

      {/* „Benachrichtigen beim Verlassen" Bestätigung */}
      <ConfirmDialog
        open={notifyPromptOpen}
        title={t("weeklyPlan.notifyOnChange.title")}
        message={t("weeklyPlan.notifyOnChange.message")}
        confirmLabel={t("weeklyPlan.notifyOnChange.confirm")}
        declineLabel={t("weeklyPlan.notifyOnChange.decline")}
        pending={false}
        onCancel={cancelNotifyPrompt}
        onDecline={declineNotifyAndLeave}
        onConfirm={confirmNotifyAndLeave}
      />

      {/* Snackbar */}
      <Snackbar
        open={snack != null}
        autoHideDuration={2200}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack != null ? (
          <Alert
            severity={snack.variant}
            variant="filled"
            onClose={() => setSnack(null)}
            sx={{ width: "100%" }}
          >
            {snack.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
